<script>
	import { onMount, onDestroy } from "svelte";
	import { getApiBase, getCurrentStore, safeApiFetch, getCurrentUser } from "../services/api";
	import Icon from "../components/icons/Icon.svelte";
	import QrConnectModal from "../components/whatsapp/QrConnectModal.svelte";
	import ChatSidebar from "../components/whatsapp/ChatSidebar.svelte";
	import ChatConversation from "../components/whatsapp/ChatConversation.svelte";
	import CustomerDrawer from "../components/whatsapp/CustomerDrawer.svelte";
	import SimulationModal from "../components/whatsapp/SimulationModal.svelte";

	export let currentUser = null;
	export let isBotActive = true;
	export let onNotify = (msg, type) => {};
	export let whatsappStatus = "Desconectado"; // Inicia buscando status real do backend

	// Modos de conexão: 'qrcode' ou 'pairing-code'
	let connectionTab = "qrcode";
	let isConnecting = false;
	let qrCountdown = 20;
	let qrTimer = null;
	let phoneInput = "";
	let pairingCode = "";
	let showPairingCode = false;
	let qrCodeDataUrl = null;
	let pollInterval = null;

	// Dados da Sessão Conectada
	let connectedPhone = "";
	let batteryLevel = 100;
	let deviceName = "WhatsApp Multi-Device";

	// Chats - Inicia limpo para nova instalação
	let chats = [];

	let activeChatId = "";
	$: activeChat = chats.find(c => c.id === activeChatId) || (chats.length > 0 ? chats[0] : null);
	let messageInput = "";
	let isAiTyping = false;

	// Estados do Redesenho Premium
	let searchQuery = "";
	let chatFilter = "all"; // 'all', 'waiting', 'mine', 'bot' (ou 'human' legado)
	let showCustomerDrawer = false;
	let showSimulationModal = false;
	let simulationCustomMsg = "";

	// Ticker local e timestamp de referência para tempo de espera (Fase 2C.3)
	let now = Date.now();
	let waitingTicker = null;

	// Identidade reativa do operador logado (Fase 2C.2)
	$: effectiveUser = currentUser || getCurrentUser() || null;
	$: currentUserId = effectiveUser?.id || null;

	// Contadores reativos para os filtros derivados de chats e currentUser (Fase 2C.2)
	$: allCount = chats.length;
	$: waitingCount = chats.filter(c => c.status === 'human_requested' || c.needsHuman === true).length;
	$: mineCount = chats.filter(c => Boolean(c.assignedOperatorId && currentUserId && c.assignedOperatorId === currentUserId)).length;
	$: botCount = chats.filter(c => c.botActive === true && c.needsHuman !== true).length;

	// Compatibilidade legada
	$: humanChatsCount = chats.filter(c => c.needsHuman || !c.botActive).length;
	$: botChatsCount = botCount;

	// Filtro inteligente de conversas (Fase 2C.2)
	$: filteredChats = chats.filter(c => {
		if (searchQuery) {
			const q = searchQuery.toLowerCase();
			const matchName = (c.name || "").toLowerCase().includes(q);
			const matchPhone = (c.phone || "").includes(q);
			const matchSnippet = (c.lastSnippet || "").toLowerCase().includes(q);
			if (!matchName && !matchPhone && !matchSnippet) return false;
		}
		if (chatFilter === "waiting") {
			return c.status === "human_requested" || c.needsHuman === true;
		}
		if (chatFilter === "mine") {
			return Boolean(c.assignedOperatorId && currentUserId && c.assignedOperatorId === currentUserId);
		}
		if (chatFilter === "bot") {
			return c.botActive === true && c.needsHuman !== true;
		}
		if (chatFilter === "human") {
			return c.status === "human_requested" || c.needsHuman === true || !c.botActive;
		}
		return true;
	});

	// Helper local para calcular e formatar o tempo de espera (Fase 2C.3)
	function formatWaitingTime(humanRequestedAt, currentNow = now) {
		if (!humanRequestedAt) return "Aguardando";
		const requestedTime = new Date(humanRequestedAt).getTime();
		if (isNaN(requestedTime)) return "Aguardando";
		const diffMinutes = Math.floor(Math.max(0, currentNow - requestedTime) / 60000);
		if (diffMinutes < 1) {
			return "Aguardando agora";
		}
		if (diffMinutes === 1) {
			return "Aguardando há 1 min";
		}
		return `Aguardando há ${diffMinutes} min`;
	}

	// Detecção de Pedido e Dados no Chat Ativo
	$: extractedOrderFromChat = (() => {
		if (!activeChat || !activeChat.messages) return null;
		for (let i = activeChat.messages.length - 1; i >= 0; i--) {
			const m = activeChat.messages[i];
			if (m.text && (m.text.includes("PEDIDO #") || m.text.includes("CONFIRMADO COM SUCESSO"))) {
				const numMatch = m.text.match(/#(\d+)/);
				const totalMatch = m.text.match(/TOTAL:\s*R\$\s*([\d,.]+)/i);
				const payMatch = m.text.match(/Forma de Pagamento:\s*([^\n\.]+)/i);
				return {
					orderNumber: numMatch ? numMatch[1] : null,
					total: totalMatch ? totalMatch[1] : null,
					paymentMethod: payMatch ? payMatch[1].trim() : null,
					fullSnippet: m.text
				};
			}
		}
		return null;
	})();

	// Atalhos Rápidos (Macros) de Atendimento
	const quickMacros = [
		{ label: "Cardápio", text: "Segue o nosso cardápio digital completo para você escolher suas delícias: https://matafome.com.br/cardapio", icon: "book-open" },
		{ label: "Chave PIX", text: "Nossa chave PIX oficial é o CNPJ/Telefone cadastrado. Ao realizar a transferência, favor enviar o comprovante aqui no chat!", icon: "key" },
		{ label: "Previsão (40-50m)", text: "Seu pedido está em produção com todo carinho! A previsão média de entrega é de 40 a 50 minutos.", icon: "clock" },
		{ label: "Pedir Endereço", text: "Poderia por gentileza nos confirmar seu endereço completo com número, bairro e ponto de referência?", icon: "map-pin" },
		{ label: "Saiu p/ Entrega", text: "Boas notícias! O entregador acabou de sair com o seu pedido a caminho do seu endereço.", icon: "bike" }
	];

	function applyMacro(macroText) {
		messageInput = macroText;
	}

	// Formatador do Markdown oficial do WhatsApp (*negrito*, _itálico_, quebras de linha)
	function formatWhatsAppText(raw) {
		if (!raw) return "";
		let text = String(raw)
			.replace(/&/g, "&amp;")
			.replace(/</g, "&lt;")
			.replace(/>/g, "&gt;");

		// *negrito* -> <strong>
		text = text.replace(/\*([^\*\n]+)\*/g, '<strong class="wa-bold">$1</strong>');
		// _itálico_ -> <em>
		text = text.replace(/_([^_\n]+)_/g, '<em class="wa-italic">$1</em>');
		// ~tachado~ -> <del>
		text = text.replace(/~([^~\n]+)~/g, '<del class="wa-strike">$1</del>');
		// Quebras de linha
		text = text.replace(/\n/g, '<br/>');

		return text;
	}

	function cleanSnippet(text) {
		if (!text) return "";
		return String(text).replace(/[*_~]/g, '').trim();
	}

	function formatPhoneNumber(phone) {
		if (!phone) return "";
		const clean = phone.replace(/\D/g, "");
		if (clean.length === 13 && clean.startsWith("55")) {
			return `+55 (${clean.slice(2, 4)}) ${clean.slice(4, 9)}-${clean.slice(9)}`;
		}
		if (clean.length === 12 && clean.startsWith("55")) {
			return `+55 (${clean.slice(2, 4)}) ${clean.slice(4, 8)}-${clean.slice(8)}`;
		}
		if (clean.length === 11) {
			return `(${clean.slice(0, 2)}) ${clean.slice(2, 7)}-${clean.slice(7)}`;
		}
		if (clean.length === 10) {
			return `(${clean.slice(0, 2)}) ${clean.slice(2, 6)}-${clean.slice(6)}`;
		}
		return phone;
	}

	function getInitials(name) {
		if (!name) return "CL";
		const clean = name.replace(/[^\w\s]/gi, '').trim();
		const parts = clean.split(/\s+/).filter(Boolean);
		if (parts.length === 0) return "CL";
		if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
		return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
	}

	function startQRCountdown() {
		stopQRCountdown();
		qrCountdown = 25;
		qrTimer = setInterval(() => {
			if (qrCountdown > 1) {
				qrCountdown -= 1;
			} else {
				stopQRCountdown();
				qrCountdown = 0;
			}
		}, 1000);
	}

	function stopQRCountdown() {
		if (qrTimer) {
			clearInterval(qrTimer);
			qrTimer = null;
		}
	}

	async function fetchWhatsAppStatus() {
		try {
			const res = await safeApiFetch('/api/whatsapp/status');
			const data = await res.json();
			if (data) {
				whatsappStatus = data.status;
				if (data.phone) connectedPhone = data.phone;
				qrCodeDataUrl = data.qrCodeDataUrl || null;
				if (data.pairingCode) {
					pairingCode = data.pairingCode;
					showPairingCode = true;
				} else if (whatsappStatus === "Conectado") {
					pairingCode = "";
					showPairingCode = false;
				}
				if (typeof data.isBotActive === "boolean") isBotActive = data.isBotActive;
				if (whatsappStatus === "Conectado") {
					qrCodeDataUrl = null;
					stopQRCountdown();
				}
			}
		} catch (err) {
			// Backend offline momentaneamente
		}
	}

	async function startRealConnection(phone = null, forceNew = false) {
		isConnecting = true;
		if (forceNew) {
			qrCodeDataUrl = null;
			stopQRCountdown();
		}
		try {
			const res = await safeApiFetch('/api/whatsapp/connect', {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ phoneNumber: phone, forceNew })
			});
			const data = await res.json();
			qrCodeDataUrl = data.qrCodeDataUrl || null;
			if (data.status) whatsappStatus = data.status;
			if (data.pairingCode) {
				pairingCode = data.pairingCode;
				showPairingCode = true;
			}
			if (qrCodeDataUrl) {
				startQRCountdown();
			}
			if (onNotify && forceNew) onNotify("Gerando novo QR Code oficial...", "info", 3);
		} catch (err) {
			console.error("Erro ao conectar WhatsApp:", err);
			if (onNotify) onNotify("Erro ao comunicar com o servidor WhatsApp.", "error", 4);
		} finally {
			isConnecting = false;
		}
	}

	let isDisconnecting = false;

	async function disconnectWhatsApp() {
		if (confirm("Deseja realmente desconectar o seu número de WhatsApp? O robô de IA será pausado até reconectar.")) {
			isDisconnecting = true;
			try {
				const res = await safeApiFetch('/api/whatsapp/disconnect', { method: "POST", headers: { "Content-Type": "application/json" } });
				if (res && !res.ok) {
					const data = await res.json().catch(() => ({}));
					console.error("Falha ao desconectar no backend:", data);
				}
				whatsappStatus = "Desconectado";
				qrCodeDataUrl = null;
				connectedPhone = "";
				stopQRCountdown();
				if (onNotify) onNotify("WhatsApp desconectado com sucesso.", "warning", 4);
			} catch (err) {
				console.error("Erro ao desconectar:", err);
				whatsappStatus = "Desconectado";
				qrCodeDataUrl = null;
				connectedPhone = "";
				stopQRCountdown();
			} finally {
				isDisconnecting = false;
			}
		}
	}

	async function generatePairingCode() {
		if (!phoneInput || phoneInput.replace(/\D/g, "").length < 10) {
			alert("Informe o número com DDD (ex: 11988887777)");
			return;
		}
		isConnecting = true;
		await startRealConnection(phoneInput, true);
	}

	function refreshQRCode() {
		startRealConnection(null, true);
	}

	async function toggleCustomerBot() {
		if (!activeChat) return;
		const nextState = !activeChat.botActive;
		activeChat.botActive = nextState;
		if (nextState) activeChat.needsHuman = false;
		chats = [...chats];

		try {
			const phone = activeChat.phone.replace(/\D/g, "");
			if (phone) {
				await safeApiFetch(`/api/whatsapp/chats/${phone}/toggle-bot`, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ botActive: nextState })
				});
			}
			if (onNotify) {
				onNotify(nextState ? "Robô de IA reativado para este cliente!" : "Robô pausado! Agora você está no controle manual.", "info", 4);
			}
		} catch (e) {
			console.error("Erro ao alternar bot no backend:", e);
		}
	}

	let pendingMediaUrl = "";

	function promptMediaAttachment() {
		const url = prompt("Cole ou digite a URL da imagem (JPG/PNG/WebP):", pendingMediaUrl || "");
		if (url !== null) {
			pendingMediaUrl = url.trim();
		}
	}

	let isSendingMessage = false;

	async function handleSendMessage() {
		if (isSendingMessage) return;
		if (!activeChat) return;
		const sentText = (messageInput || "").trim();
		const sentMedia = (pendingMediaUrl || "").trim() || null;
		if (!sentText && !sentMedia) return;

		// ETAPA B: Validar conexão ANTES de criar o balão otimista ou limpar o input
		if (whatsappStatus !== "Conectado") {
			if (onNotify) {
				onNotify("WhatsApp desconectado. Conecte seu aparelho antes de enviar mensagens.", "warning", 5);
			}
			// Preserva messageInput intacto para que o atendente não perca o texto digitado
			return;
		}

		const phone = (activeChat.phone || "").replace(/\D/g, "");
		if (!phone) {
			if (onNotify) {
				onNotify("Telefone do cliente inválido para envio.", "error", 4);
			}
			return;
		}

		// Trava de submissão para prevenir cliques duplos concorrentes
		isSendingMessage = true;

		// Prompt 10.3.3: Captura inequívoca da identidade da conversa de destino ANTES de qualquer await
		const targetChatId = activeChat.id;
		const targetPhone = phone;

		// Validação de conexão e dados concluída: limpa o campo e gera identificador local único
		messageInput = "";
		pendingMediaUrl = "";

		const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
		const optimisticMsg = {
			id: tempId,
			tempId,
			clientTempId: tempId,
			transportMessageId: null,
			localMessageId: null,
			fromMe: true,
			isOperator: true,
			isBot: false,
			status: 'sending', // 'sending' | 'sent' | 'warning' | 'failed' | 'unknown'
			time: new Date().toLocaleTimeString().slice(0, 5),
			text: sentText,
			mediaUrl: sentMedia,
			warning: null,
			error: null
		};

		activeChat.messages = [...(activeChat.messages || []), optimisticMsg];
		activeChat.lastSnippet = sentText || (sentMedia ? '📷 [Imagem]' : '');
		// Ao atendente humano responder, assume o chat e pausa a IA para este cliente
		activeChat.botActive = false;
		activeChat.needsHuman = false;
		chats = [...chats];

		try {
			const res = await safeApiFetch(`/api/whatsapp/chats/${targetPhone}/send-message`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ text: sentText, mediaUrl: sentMedia, clientTempId: tempId })
			});

			// Prompt 10.3.3: Resolver a conversa de destino de maneira inequívoca no estado atual de chats.
			// NUNCA utilizar activeChat após o await, pois o atendente pode ter selecionado outra conversa.
			const targetChat = chats.find(c =>
				(targetChatId && c.id === targetChatId) ||
				(targetPhone && c.phone && c.phone.replace(/\D/g, "") === targetPhone)
			);

			// Se a conversa original foi removida durante a requisição, não corrompe nenhuma outra conversa
			if (!targetChat) {
				console.warn(`[WhatsApp] Conversa original (${targetChatId || targetPhone}) não encontrada após requisição HTTP. Nenhuma conversa ativa foi modificada.`);
				return;
			}

			if (res && res.ok) {
				let data = null;
				try {
					data = await res.json();
				} catch (_) {
					data = null;
				}

				// PARTE B: Resposta HTTP 200 com corpo vazio ou JSON inválido não pode ser falso sucesso
				if (!data || typeof data !== 'object' || (!data.success && !data.transportMessageId && !data.messageId)) {
					optimisticMsg.status = 'unknown';
					optimisticMsg.error = 'Servidor retornou HTTP 200 mas com corpo inválido ou incompleto.';
					chats = [...chats];
					if (onNotify) {
						onNotify('Resposta do servidor incompleta. Não foi possível confirmar a transmissão.', 'warning', 6);
					}
					return;
				}

				// PARTE A: Reconciliação determinística por identificadores exclusivamente na conversa original (targetChat)
				// Verifica se o evento Socket.IO já chegou provisoriamente antes da resolução do HTTP
				const existingIdx = (targetChat.messages || []).findIndex(m =>
					(tempId && (m.tempId === tempId || m.clientTempId === tempId)) ||
					(data.clientTempId && (m.clientTempId === data.clientTempId || m.tempId === data.clientTempId)) ||
					(data.transportMessageId && m.transportMessageId === data.transportMessageId) ||
					(data.messageId && m.id === data.messageId) ||
					(data.localMessageId && m.localMessageId === data.localMessageId)
				);

				if (existingIdx !== -1) {
					// Socket.IO chegou primeiro ou balão otimista encontrado: consolida o balão
					const confirmedMsg = targetChat.messages[existingIdx];
					confirmedMsg.id = data.messageId || data.transportMessageId || confirmedMsg.id;
					confirmedMsg.transportMessageId = data.transportMessageId || confirmedMsg.transportMessageId || null;
					confirmedMsg.clientTempId = data.clientTempId || tempId || confirmedMsg.clientTempId || null;
					confirmedMsg.localMessageId = data.localMessageId || confirmedMsg.localMessageId || null;
					confirmedMsg.status = data.persisted === false ? 'warning' : 'sent';
					if (data.persisted === false && data.warning) {
						confirmedMsg.warning = data.warning;
					}
					const optIdx = targetChat.messages.findIndex((m, idx) => idx !== existingIdx && (m.id === tempId || m.tempId === tempId || (tempId && m.clientTempId === tempId)));
					if (optIdx !== -1) {
						targetChat.messages = targetChat.messages.filter((_, idx) => idx !== optIdx);
					}
				} else {
					// HTTP respondeu primeiro: atualiza a tentativa otimista com os identificadores oficiais
					optimisticMsg.id = data.messageId || data.transportMessageId || optimisticMsg.id;
					optimisticMsg.clientTempId = data.clientTempId || tempId;
					optimisticMsg.transportMessageId = data.transportMessageId || null;
					optimisticMsg.localMessageId = data.localMessageId || null;
					optimisticMsg.status = data.persisted === false ? 'warning' : 'sent';
					if (data.persisted === false && data.warning) {
						optimisticMsg.warning = data.warning;
					}
				}

				if (data.persisted === false) {
					if (onNotify) {
						onNotify(data.warning || "Mensagem transmitida ao WhatsApp, mas histórico local pendente de registro.", "warning", 6);
					}
				}
				chats = [...chats];
			} else {
				// Rejeição HTTP: verificar se o resultado é incerto (504 ou payload status: "uncertain")
				let errData = {};
				try {
					if (res && typeof res.json === 'function') {
						errData = await res.json();
					}
				} catch (_) {
					errData = {};
				}

				const isUncertain = res?.status === 504 || errData?.status === 'uncertain' || errData?.code === 'TRANSPORT_UNCERTAIN';
				if (isUncertain) {
					const uncertainMsg = errData?.error || "O envio pode ter ocorrido. Verifique a conversa antes de tentar reenviar.";
					optimisticMsg.status = 'unknown';
					optimisticMsg.error = uncertainMsg;
					chats = [...chats];
					if (onNotify) {
						onNotify(uncertainMsg, "warning", 6);
					}
				} else {
					const errMsg = errData?.error || (res ? `Falha no envio (HTTP ${res.status}).` : "Falha ao enviar mensagem.");
					optimisticMsg.status = 'failed';
					optimisticMsg.error = errMsg;
					chats = [...chats];
					if (onNotify) {
						onNotify(errMsg, "error", 6);
					}
				}
			}
		} catch (err) {
			// Timeout (AbortError), erro de rede ou resultado ambíguo
			console.error("Erro ou timeout ao enviar mensagem pelo backend:", err);
			optimisticMsg.status = 'unknown';
			optimisticMsg.error = "Resultado não confirmado. O envio pode ter ocorrido; verifique a conversa antes de tentar reenviar.";
			chats = [...chats];
			if (onNotify) {
				onNotify("Envio não pôde ser confirmado (conexão instável ou tempo limite). Não tente reenviar sem verificar.", "warning", 6);
			}
		} finally {
			isSendingMessage = false;
		}
	}

	function retryFailedMessage(msg) {
		if (!msg) return;
		if (activeChat && activeChat.messages) {
			activeChat.messages = activeChat.messages.filter(m => m !== msg);
			chats = [...chats];
		}
		messageInput = msg.text || "";
	}

	async function sendCustomerMessageToAI(customText) {
		const textToSend = (customText || messageInput || "").trim();
		if (!textToSend) return;

		messageInput = "";

		// 1. Mensagem do Cliente no WhatsApp (Balão Esquerdo)
		activeChat.messages = [
			...activeChat.messages,
			{ fromMe: false, time: new Date().toLocaleTimeString().slice(0, 5), text: textToSend }
		];
		activeChat.lastSnippet = textToSend;
		chats = [...chats];

		// 2. Se o bot estiver pausado para este chat, não responde automaticamente
		if (!isBotActive || !activeChat.botActive) {
			if (onNotify) onNotify("Mensagem recebida! O robô de IA está pausado, responda manualmente.", "warning", 3);
			return;
		}

		isAiTyping = true;

		try {
			const store = getCurrentStore();
			const res = await safeApiFetch('/api/ai/chat', {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					storeId: store?.id || "",
					customerPhone: (activeChat.phone || "").replace(/\D/g, "") || "5511999999999",
					customerName: activeChat.name || "Cliente",
					message: textToSend
				})
			});

			const data = await res.json();
			isAiTyping = false;

			if (data.reply) {
				const nowTime = new Date().toLocaleTimeString().slice(0, 5);
				const newMessages = [
					{
						fromMe: true,
						isBot: true,
						time: nowTime,
						text: data.reply
					}
				];

				if (data.extraMessages && Array.isArray(data.extraMessages)) {
					for (const extra of data.extraMessages) {
						newMessages.push({
							fromMe: true,
							isBot: true,
							time: nowTime,
							text: extra
						});
					}
				}

				activeChat.messages = [
					...activeChat.messages,
					...newMessages
				];
				activeChat.lastSnippet = (data.extraMessages && data.extraMessages.length > 0 ? data.extraMessages[0] : data.reply).slice(0, 45) + "...";
				chats = [...chats];

				if (data.intent === "order_created" && data.order) {
					if (onNotify) {
						onNotify(`NOVO PEDIDO #${data.order.order_number} gerado pela IA! Enviado para o Kanban.`, "success", 6);
					}
				}
			}
		} catch (err) {
			isAiTyping = false;
			console.error("Erro ao chamar motor de IA:", err);
			activeChat.messages = [
				...activeChat.messages,
				{
					fromMe: true,
					isBot: true,
					time: new Date().toLocaleTimeString().slice(0, 5),
					text: "Desculpe, tive uma instabilidade momentânea ao consultar o cardápio. Um atendente humano responderá em instantes."
				}
			];
			chats = [...chats];
		}
	}

	function simulateIncomingCustomerMessage() {
		sendCustomerMessageToAI("Boa noite, qual o cardápio de hoje?");
	}

	async function fetchChats() {
		try {
			const res = await safeApiFetch('/api/whatsapp/chats');
			if (res && res.ok) {
				const data = await res.json();
				if (data && Array.isArray(data.chats) && data.chats.length > 0) {
					chats = data.chats;
					if (!activeChatId) {
						activeChatId = chats[0].id;
					}
				}
			}
		} catch (err) {
			console.warn("Falha ao buscar chats:", err);
		}
	}

	let loadingHistory = false;
	let loadingHistoryPhone = null;

	async function loadChatHistory(chat, options = {}) {
		if (!chat || !chat.phone) return;
		const cleanPhone = (chat.phone || "").replace(/\D/g, "");
		if (!cleanPhone) return;

		// Se já carregou inicialmente e não é paginação anterior (before), não refaz
		if (!options.before && chat._historyLoaded && !options.force) {
			return;
		}

		loadingHistory = true;
		loadingHistoryPhone = cleanPhone;

		try {
			const limit = options.limit || 30;
			let url = `/api/whatsapp/chats/${cleanPhone}/messages?limit=${limit}`;
			if (options.before) {
				url += `&before=${encodeURIComponent(options.before)}`;
			}

			const res = await safeApiFetch(url);
			if (!res || !res.ok) return;

			const data = await res.json();
			if (!data || !Array.isArray(data.messages)) return;

			// Busca a conversa atual no array chats para não sobrescrever caso o operador tenha trocado de conversa
			const targetChat = chats.find(c => {
				const cPhone = (c.phone || "").replace(/\D/g, "");
				return cPhone === cleanPhone || c.id === chat.id;
			});

			if (!targetChat) return;

			const fetchedMessages = data.messages.map(m => {
				let timeStr = "";
				try {
					timeStr = new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
				} catch (_) {
					timeStr = "";
				}

				return {
					id: m.whatsapp_message_id || m.id,
					localRecordId: m.id,
					clientTempId: m.client_temp_id || null,
					transportMessageId: m.whatsapp_message_id || null,
					fromMe: !!m.from_me,
					isOperator: !!m.is_operator,
					isBot: !!m.is_bot,
					status: m.status === 'confirmed' ? 'sent' : (m.status === 'pending' ? 'sending' : (m.status === 'uncertain' ? 'unknown' : (m.status === 'failed' ? 'failed' : 'sent'))),
					time: timeStr,
					text: m.body || "",
					mediaUrl: m.media_url || null,
					createdAt: m.created_at
				};
			});

			if (options.before) {
				// Paginação: insere mensagens anteriores no topo
				const existingIds = new Set((targetChat.messages || []).map(m => m.id || m.clientTempId || m.transportMessageId));
				const olderUnique = fetchedMessages.filter(m => !existingIds.has(m.id) && (!m.clientTempId || !existingIds.has(m.clientTempId)));
				targetChat.messages = [...olderUnique, ...(targetChat.messages || [])];
			} else {
				// Carga inicial: mescla mensagens do banco com eventuais tentativas otimistas pendentes locais
				const existingSending = (targetChat.messages || []).filter(m => m.status === 'sending');
				const existingIds = new Set(fetchedMessages.map(m => m.id));
				const pendingToKeep = existingSending.filter(m => !existingIds.has(m.id) && (!m.clientTempId || !fetchedMessages.some(fm => fm.clientTempId === m.clientTempId)));

				targetChat.messages = [...fetchedMessages, ...pendingToKeep];
				targetChat._historyLoaded = true;
			}

			targetChat._nextCursor = data.nextCursor || null;
			targetChat._hasMore = !!data.hasMore;
			chats = [...chats];
		} catch (err) {
			console.warn("[WhatsApp] Falha ao carregar histórico paginado:", err);
		} finally {
			loadingHistory = false;
			loadingHistoryPhone = null;
		}
	}

	$: if (activeChat && activeChat.phone && !activeChat._historyLoaded && !loadingHistory) {
		loadChatHistory(activeChat);
	}

	onMount(() => {
		// Checa o status atual do WhatsApp Real e chats
		fetchWhatsAppStatus();
		fetchChats();

		// Polling a cada 2.5 segundos para atualizar o status caso esteja conectando
		pollInterval = setInterval(fetchWhatsAppStatus, 2500);

		// Ticker local a cada 60s exclusivamente para manter o tempo de espera visualmente atualizado (Fase 2C.3)
		waitingTicker = setInterval(() => {
			now = Date.now();
		}, 60000);

		// Sincronização em Tempo Real via Socket.IO
		if (typeof window !== "undefined" && window.io) {
			try {
				const socket = window.io(getApiBase());

				socket.on("connect", () => {
					const store = getCurrentStore();
					const token = localStorage.getItem("matafome_token") || "";
					if (store?.id) {
						socket.emit("join_store", { 
							storeId: store.id,
							accessKey: store.access_key,
							token: token
						});
					}
				});

				socket.on("whatsapp_qr", (payload) => {
					qrCodeDataUrl = payload.qrCodeDataUrl || null;
					if (payload.status) {
						whatsappStatus = payload.status;
					} else {
						whatsappStatus = payload.qrCodeDataUrl ? "Conectando" : "Desconectado";
					}
					if (payload.qrCodeDataUrl) {
						startQRCountdown();
					} else {
						stopQRCountdown();
					}
				});

				socket.on("whatsapp_status", (payload) => {
					if (payload.status) whatsappStatus = payload.status;
					if (payload.phone) connectedPhone = payload.phone;
					if (payload.status === "Conectado") {
						qrCodeDataUrl = null;
						stopQRCountdown();
						if (onNotify) {
							onNotify(`WhatsApp Conectado com sucesso (${payload.phone})! O robô de IA já está ativo.`, "success", 6);
						}
					} else if (payload.status === "Desconectado") {
						stopQRCountdown();
						qrCodeDataUrl = null;
					}
				});

				socket.on("whatsapp_pairing_code", (payload) => {
					if (payload.pairingCode) {
						pairingCode = payload.pairingCode;
						showPairingCode = true;
					}
				});

				// Mensagem recebida de um cliente real
				socket.on("whatsapp_message_received", (msg) => {
					const cleanMsgPhone = (msg.phone || '').replace(/\D/g, '');
					let chat = chats.find(c => {
						if (msg.chatId && c.id === msg.chatId) return true;
						const cleanChatPhone = (c.phone || '').replace(/\D/g, '');
						if (cleanMsgPhone && cleanChatPhone) {
							return cleanChatPhone.includes(cleanMsgPhone) || cleanMsgPhone.includes(cleanChatPhone);
						}
						return false;
					});
					if (!chat) {
						chat = {
							id: msg.chatId || `chat-${Date.now()}`,
							name: msg.name || `Cliente (${msg.phone})`,
							phone: msg.phone,
							time: msg.time || new Date().toLocaleTimeString().slice(0, 5),
							avatar: "",
							avatarUrl: msg.avatarUrl || null,
							unread: 1,
							botActive: true,
							lastSnippet: msg.text,
							messages: []
						};
						chats = [chat, ...chats];
					} else {
						if (msg.name && (chat.name === 'Cliente' || chat.name.startsWith('Cliente ('))) {
							chat.name = msg.name;
						}
						if (msg.avatarUrl && !chat.avatarUrl) {
							chat.avatarUrl = msg.avatarUrl;
						}
					}

					// Deduplicação: se a última mensagem recebida já é idêntica, evita duplicar
					const lastMsg = chat.messages && chat.messages.length > 0 ? chat.messages[chat.messages.length - 1] : null;
					if (lastMsg && !lastMsg.fromMe && lastMsg.text?.trim() === (msg.text || '').trim()) {
						return;
					}

					chat.messages = [
						...chat.messages,
						{ fromMe: false, time: msg.time || new Date().toLocaleTimeString().slice(0, 5), text: msg.text, mediaUrl: msg.mediaUrl || null }
					];
					chat.lastSnippet = msg.text;
					chat.time = msg.time || new Date().toLocaleTimeString().slice(0, 5);
					chats = [...chats];
					if (onNotify) {
						onNotify(`Nova mensagem no WhatsApp de ${msg.name || msg.phone}: "${msg.text.slice(0, 30)}..."`, "info", 4);
					}
				});

				// Mensagem enviada pelo robô ou atendente para o cliente real
				socket.on("whatsapp_message_sent", (msg) => {
					const cleanMsgPhone = (msg.phone || '').replace(/\D/g, '');
					let chat = chats.find(c => {
						if (msg.chatId && c.id === msg.chatId) return true;
						const cleanChatPhone = (c.phone || '').replace(/\D/g, '');
						if (cleanMsgPhone && cleanChatPhone) {
							return cleanChatPhone.includes(cleanMsgPhone) || cleanMsgPhone.includes(cleanChatPhone);
						}
						return false;
					});
					if (!chat) {
						chat = {
							id: msg.chatId || msg.phone || `chat-${Date.now()}`,
							name: msg.name || `Cliente (${msg.phone})`,
							phone: msg.phone,
							time: msg.time || new Date().toLocaleTimeString().slice(0, 5),
							avatar: "",
							unread: 0,
							botActive: !msg.isOperator,
							needsHuman: false,
							lastSnippet: (msg.text || '').slice(0, 45) + "...",
							messages: []
						};
						chats = [chat, ...chats];
					}

					// ETAPA D: Reconciliação robusta sem comparação exclusiva de texto
					// 1. Procura por identificadores únicos compartilhados
					let matchedMsg = null;
					if (msg.clientTempId) {
						matchedMsg = (chat.messages || []).find(m => m.clientTempId === msg.clientTempId || m.tempId === msg.clientTempId || m.id === msg.clientTempId);
					}
					if (!matchedMsg && msg.transportMessageId) {
						matchedMsg = (chat.messages || []).find(m => m.transportMessageId === msg.transportMessageId || m.id === msg.transportMessageId);
					}
					if (!matchedMsg && msg.messageId) {
						matchedMsg = (chat.messages || []).find(m => m.id === msg.messageId);
					}
					if (!matchedMsg && msg.localMessageId) {
						matchedMsg = (chat.messages || []).find(m => m.localMessageId === msg.localMessageId);
					}

					if (matchedMsg) {
						matchedMsg.id = msg.messageId || msg.transportMessageId || matchedMsg.id;
						matchedMsg.clientTempId = msg.clientTempId || matchedMsg.clientTempId || null;
						matchedMsg.transportMessageId = msg.transportMessageId || matchedMsg.transportMessageId || null;
						matchedMsg.localMessageId = msg.localMessageId || matchedMsg.localMessageId || null;
						matchedMsg.status = msg.persisted === false ? 'warning' : 'sent';
						matchedMsg.isOperator = !!msg.isOperator;
						matchedMsg.isBot = !!msg.isBot;
						if (msg.mediaUrl) matchedMsg.mediaUrl = msg.mediaUrl;
						if (msg.time) matchedMsg.time = msg.time;
						chats = [...chats];
						return;
					}

					// 3. Nenhuma correspondência comprovada: insere mensagem legítima nova
					chat.messages = [
						...(chat.messages || []),
						{
							id: msg.messageId || msg.transportMessageId || `srv_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
							clientTempId: msg.clientTempId || null,
							transportMessageId: msg.transportMessageId || null,
							localMessageId: msg.localMessageId || null,
							fromMe: true,
							isBot: !!msg.isBot,
							isOperator: !!msg.isOperator,
							status: msg.persisted === false ? 'warning' : 'sent',
							time: msg.time || new Date().toLocaleTimeString().slice(0, 5),
							text: msg.text,
							mediaUrl: msg.mediaUrl || null
						}
					];
					chat.lastSnippet = (msg.text || '').slice(0, 45) + "...";
					chats = [...chats];
				});

				// Sincronização direta de chat atualizado
				socket.on("chat_updated", (updatedChat) => {
					if (!updatedChat) return;
					const cleanUpdatedPhone = (updatedChat.phone || '').replace(/\D/g, '');
					const idx = chats.findIndex(c => {
						if (c.id === updatedChat.id) return true;
						const cPhone = (c.phone || '').replace(/\D/g, '');
						if (cleanUpdatedPhone && cPhone) {
							return cPhone.includes(cleanUpdatedPhone) || cleanUpdatedPhone.includes(cPhone);
						}
						return false;
					});
					if (idx !== -1) {
						const currentChat = chats[idx];
						const sendingMessages = (currentChat.messages || []).filter(m => m.status === 'sending');
						let mergedMessages = updatedChat.messages || currentChat.messages || [];
						if (sendingMessages.length > 0) {
							for (const sMsg of sendingMessages) {
								const existsInUpdated = mergedMessages.some(m =>
									(sMsg.transportMessageId && m.transportMessageId === sMsg.transportMessageId) ||
									(sMsg.localMessageId && m.localMessageId === sMsg.localMessageId) ||
									(sMsg.id && m.id === sMsg.id)
								);
								if (!existsInUpdated) {
									mergedMessages.push(sMsg);
								}
							}
						}
						mergedMessages = mergedMessages.map(m => {
							if (m.fromMe && !m.status) {
								return { ...m, status: m.persisted === false ? 'warning' : 'sent' };
							}
							return m;
						});
						chats[idx] = { ...currentChat, ...updatedChat, messages: mergedMessages };
					} else {
						chats = [updatedChat, ...chats];
					}
					chats = [...chats];
				});

				// Evento de exclusão de chat (ex: fusão de chat LID temporário para telefone canônico)
				socket.on("chat_deleted", (payload) => {
					if (!payload) return;
					const targetId = payload.chatId || payload.phone;
					if (!targetId) return;
					const cleanTarget = String(targetId).replace(/\D/g, '');
					chats = chats.filter(c => {
						const cPhone = (c.phone || '').replace(/\D/g, '');
						return c.id !== targetId && cPhone !== cleanTarget;
					});
					if (activeChatId === targetId || activeChatId === cleanTarget) {
						if (payload.mergedInto) {
							activeChatId = payload.mergedInto;
						} else if (chats.length > 0) {
							activeChatId = chats[0].id;
						} else {
							activeChatId = "";
						}
					}
				});

				// Evento de fusão de conversas em tempo real
				socket.on("chats_merged", (payload) => {
					if (!payload) return;
					const fromId = payload.fromChatId;
					const toId = payload.toChatId;
					if (fromId) {
						const cleanFrom = String(fromId).replace(/\D/g, '');
						chats = chats.filter(c => {
							const cPhone = (c.phone || '').replace(/\D/g, '');
							return c.id !== fromId && cPhone !== cleanFrom;
						});
					}
					if (activeChatId === fromId) {
						activeChatId = toId || (chats.length > 0 ? chats[0].id : "");
					}
					const mergedTarget = chats.find(c => c.id === toId || (c.phone || '').replace(/\D/g, '') === String(toId).replace(/\D/g, ''));
					if (mergedTarget && activeChatId === mergedTarget.id) {
						loadChatHistory(mergedTarget, { force: true });
					}
				});

				// Alerta de cliente pedindo atendimento humano
				socket.on("human_takeover_requested", (data) => {
					const cleanDataPhone = (data.phone || '').replace(/\D/g, '');
					let chat = chats.find(c => {
						if (data.chatId && c.id === data.chatId) return true;
						const cPhone = (c.phone || '').replace(/\D/g, '');
						if (cleanDataPhone && cPhone) {
							return cPhone.includes(cleanDataPhone) || cleanDataPhone.includes(cPhone);
						}
						return false;
					});
					if (!chat) {
						chat = {
							id: data.chatId || data.phone,
							name: data.name || `Cliente (${data.phone})`,
							phone: data.phone,
							time: data.time || new Date().toLocaleTimeString().slice(0, 5),
							avatar: "",
							unread: 1,
							botActive: false,
							needsHuman: true,
							lastSnippet: data.lastMessage || "Solicitou atendente humano",
							messages: []
						};
						chats = [chat, ...chats];
					} else {
						chat.needsHuman = true;
						chat.botActive = false;
					}
					chats = [...chats];
					activeChatId = chat.id;
					if (onNotify) {
						onNotify(`ATENÇÃO: ${data.name || data.phone} pediu Atendimento Humano no WhatsApp!`, "warning", 8);
					}
				});
			} catch (e) {
				console.warn("Socket.io não pôde ser conectado:", e);
			}
		}
	});

	onDestroy(() => {
		if (pollInterval) clearInterval(pollInterval);
		if (waitingTicker) clearInterval(waitingTicker);
		stopQRCountdown();
	});
</script>

<div class="whatsapp-view-container" class:has-active-chat={!!activeChat}>
	<!-- Header Unificado e Profissional da Central -->
	<header class="whatsapp-header">
		<div class="header-titles">
			<div class="header-badge-row">
				<div class="header-badge">
					<Icon name="whatsapp" size={13} />
					<span>Central de Atendimento & IA</span>
				</div>

				<!-- Indicador Semântico de Status com Pulso -->
				<div class="conn-status-pill status-{whatsappStatus.toLowerCase()}">
					<span class="pulse-dot"></span>
					<span class="status-label">
						{#if whatsappStatus === "Conectado"}
							Conectado {#if connectedPhone}({formatPhoneNumber(connectedPhone)}){/if}
						{:else if whatsappStatus === "Conectando"}
							Conectando...
						{:else}
							Desconectado
						{/if}
					</span>
				</div>

				{#if whatsappStatus === "Conectado"}
					<div class="bot-global-indicator" class:active={isBotActive}>
						<Icon name="bot" size={12} />
						<span>{isBotActive ? 'Robô de IA Ativo' : 'Robô Pausado'}</span>
					</div>
				{/if}
			</div>

			<h1>WhatsApp</h1>
			<p class="subtitle">Central de atendimento, conversas e pedidos em tempo real</p>
		</div>

		<div class="header-actions">
			<button class="btn-refresh" on:click={fetchWhatsAppStatus} title="Atualizar status do WhatsApp">
				<Icon name="refresh" size={14} />
				<span>Atualizar</span>
			</button>

			{#if whatsappStatus === "Conectado"}
				<button class="btn-action-outline danger" on:click={disconnectWhatsApp} disabled={isDisconnecting} title="Desconectar WhatsApp">
					{#if isDisconnecting}
						<Icon name="refresh" size={14} className="spin" />
						<span>Desconectando...</span>
					{:else}
						<Icon name="x" size={14} />
						<span>Desconectar</span>
					{/if}
				</button>
			{:else}
				<button class="btn-primary" on:click={() => startRealConnection(null, true)} disabled={isConnecting}>
					{#if isConnecting}
						<Icon name="refresh" size={14} className="spin" />
						<span>Conectando...</span>
					{:else}
						<Icon name="play" size={14} />
						<span>Conectar WhatsApp</span>
					{/if}
				</button>
			{/if}
		</div>
	</header>

	<!-- ======================================================= -->
	<!-- CASO 1: WHATSAPP DESCONECTADO (TELA OFICIAL DE CONEXÃO) -->
	<!-- ======================================================= -->
	{#if whatsappStatus !== "Conectado"}
		<QrConnectModal
			bind:connectionTab={connectionTab}
			{qrCodeDataUrl}
			{qrCountdown}
			{isConnecting}
			bind:phoneInput={phoneInput}
			{showPairingCode}
			{pairingCode}
			on:switchTab={(e) => (connectionTab = e.detail)}
			on:refreshQRCode={refreshQRCode}
			on:startConnection={() => startRealConnection(null, true)}
			on:generatePairingCode={generatePairingCode}
		/>
	{/if}

	<!-- ======================================================= -->
	<!-- CASO 2: WHATSAPP CONECTADO (CHATS, IA & TRANSBORDO)     -->
	<!-- ======================================================= -->
	{#if whatsappStatus === "Conectado"}
		<div class="whatsapp-main-grid" class:drawer-open={showCustomerDrawer}>
			<!-- Coluna 1: Lista Lateral de Conversas -->
			<ChatSidebar
				bind:searchQuery={searchQuery}
				bind:chatFilter={chatFilter}
				bind:activeChatId={activeChatId}
				{allCount}
				{waitingCount}
				{mineCount}
				{botCount}
				{filteredChats}
				{activeChat}
				{currentUserId}
				{now}
				{getInitials}
				{cleanSnippet}
				{formatWaitingTime}
				on:selectChat={(e) => (activeChatId = e.detail)}
				on:openSimulation={() => (showSimulationModal = true)}
			/>

			<!-- Coluna 2: Chat / Área de Conversa -->
			<ChatConversation
				{activeChat}
				bind:activeChatId={activeChatId}
				{currentUserId}
				{now}
				bind:showCustomerDrawer={showCustomerDrawer}
				{loadingHistory}
				{loadingHistoryPhone}
				{isAiTyping}
				{isSendingMessage}
				bind:pendingMediaUrl={pendingMediaUrl}
				bind:messageInput={messageInput}
				{onNotify}
				{getInitials}
				{formatPhoneNumber}
				{formatWaitingTime}
				{formatWhatsAppText}
				on:backToList={() => (activeChatId = "")}
				on:toggleBot={toggleCustomerBot}
				on:toggleCustomerDrawer={(e) => (showCustomerDrawer = e.detail)}
				on:loadHistory={(e) => loadChatHistory(activeChat, e.detail)}
				on:retryMessage={(e) => retryFailedMessage(e.detail)}
				on:sendMessage={handleSendMessage}
			/>

			<!-- Coluna 3: Painel Lateral / Dossiê Retrátil do Cliente -->
			<CustomerDrawer
				show={showCustomerDrawer}
				{activeChat}
				{extractedOrderFromChat}
				{getInitials}
				{formatPhoneNumber}
				on:close={() => (showCustomerDrawer = false)}
				on:toggleBot={toggleCustomerBot}
				on:applyMacro={(e) => applyMacro(e.detail === 'menu' ? quickMacros[0].text : quickMacros[1].text)}
			/>
		</div>

		<!-- Modal de Simulação e Laboratório de Testes da IA -->
		<SimulationModal
			show={showSimulationModal}
			on:close={() => (showSimulationModal = false)}
			on:sendSimulatedMessage={(e) => sendCustomerMessageToAI(e.detail)}
		/>
	{/if}
</div>

<style>
	.whatsapp-view-container {
		display: flex;
		flex-direction: column;
		height: calc(100dvh - 36px);
		box-sizing: border-box;
		background: var(--neutral-bg, #F8FAFC);
		color: var(--text-primary, #0F172A);
		font-family: inherit;
		overflow: hidden;
	}

	/* ================= HEADER PRINCIPAL ================= */
	.whatsapp-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 16px 24px;
		background: var(--neutral-surface, #FFFFFF);
		border-bottom: 1px solid var(--neutral-border, #E2E8F0);
		flex-shrink: 0;
		flex-wrap: wrap;
		gap: 14px;
	}

	.header-titles {
		display: flex;
		flex-direction: column;
		gap: 4px;
	}

	.header-badge-row {
		display: flex;
		align-items: center;
		gap: 10px;
		flex-wrap: wrap;
	}

	.header-badge {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		background: var(--brand-primary-soft, #FFF7ED);
		color: var(--brand-primary, #FF6A00);
		border: 1px solid rgba(255, 106, 0, 0.2);
		padding: 4px 10px;
		border-radius: var(--radius-full, 9999px);
		font-size: 11.5px;
		font-weight: 700;
		text-transform: uppercase;
		letter-spacing: 0.5px;
	}

	.conn-status-pill {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 3px 10px;
		border-radius: var(--radius-full, 9999px);
		font-size: 12px;
		font-weight: 700;
	}

	.conn-status-pill.status-conectado {
		background: var(--semantic-success-soft, #ECFDF5);
		color: var(--semantic-success, #059669);
		border: 1px solid rgba(5, 150, 105, 0.25);
	}

	.conn-status-pill.status-conectando {
		background: var(--semantic-warning-soft, #FFFBEB);
		color: var(--semantic-warning, #D97706);
		border: 1px solid rgba(217, 119, 6, 0.25);
	}

	.conn-status-pill.status-desconectado {
		background: var(--semantic-danger-soft, #FEF2F2);
		color: var(--semantic-danger, #DC2626);
		border: 1px solid rgba(220, 38, 38, 0.25);
	}

	.pulse-dot {
		width: 7px;
		height: 7px;
		border-radius: 50%;
		background: currentColor;
	}

	.conn-status-pill.status-conectado .pulse-dot {
		box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.25);
		animation: pulseDot 2s infinite ease-in-out;
	}

	.conn-status-pill.status-conectando .pulse-dot {
		box-shadow: 0 0 0 3px rgba(217, 119, 6, 0.25);
		animation: pulseDot 1s infinite ease-in-out;
	}

	@keyframes pulseDot {
		0%, 100% { transform: scale(1); opacity: 1; }
		50% { transform: scale(1.3); opacity: 0.6; }
	}

	.bot-global-indicator {
		display: inline-flex;
		align-items: center;
		gap: 5px;
		background: #FAF5FF;
		color: #7E22CE;
		border: 1px solid #E9D5FF;
		padding: 3px 10px;
		border-radius: var(--radius-full, 9999px);
		font-size: 11.5px;
		font-weight: 700;
	}

	.whatsapp-header h1 {
		font-size: 24px;
		font-weight: 900;
		margin: 0;
		color: var(--text-primary, #0F172A);
		letter-spacing: -0.5px;
	}

	.subtitle {
		font-size: 13px;
		color: var(--text-secondary, #475569);
		margin: 0;
	}

	.header-actions {
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.btn-refresh {
		background: var(--neutral-surface, #FFFFFF);
		border: 1px solid var(--neutral-border, #CBD5E1);
		padding: 9px 14px;
		border-radius: var(--radius-md, 10px);
		font-size: 13px;
		font-weight: 600;
		color: var(--text-secondary, #475569);
		cursor: pointer;
		display: inline-flex;
		align-items: center;
		gap: 6px;
		transition: all 0.15s ease;
	}

	.btn-refresh:hover {
		background: var(--neutral-surface-hover, #F1F5F9);
		color: var(--text-primary, #0F172A);
	}

	.btn-action-outline {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		background: #FFFFFF;
		border: 1px solid var(--neutral-border, #CBD5E1);
		padding: 9px 14px;
		border-radius: var(--radius-md, 10px);
		font-size: 13px;
		font-weight: 600;
		color: var(--text-secondary, #475569);
		cursor: pointer;
		transition: all 0.15s ease;
	}

	.btn-action-outline:hover {
		background: var(--neutral-surface-hover, #F1F5F9);
		color: var(--text-primary, #0F172A);
	}

	.btn-action-outline.danger:hover {
		background: #FEF2F2;
		color: var(--semantic-danger, #DC2626);
		border-color: #FECACA;
	}

	.btn-action-outline.sim-btn {
		background: #FAF5FF;
		color: #7E22CE;
		border-color: #E9D5FF;
	}

	.btn-action-outline.sim-btn:hover {
		background: #7E22CE;
		color: #FFFFFF;
	}

	.btn-primary {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		background: var(--brand-primary, #FF6A00);
		color: #FFFFFF;
		border: none;
		padding: 9px 18px;
		border-radius: var(--radius-md, 10px);
		font-size: 13px;
		font-weight: 700;
		cursor: pointer;
		transition: all 0.15s ease;
	}

	.btn-primary:hover:not(:disabled) {
		background: var(--brand-primary-hover, #E85A00);
		transform: translateY(-1px);
	}

	.btn-primary:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}

	/* ================= GRID PRINCIPAL DO WHATSAPP (3 COLUNAS) ================= */
	.whatsapp-main-grid {
		display: grid;
		grid-template-columns: 340px 1fr;
		flex: 1;
		overflow: hidden;
		background: var(--neutral-bg, #F8FAFC);
		position: relative;
	}

	.whatsapp-main-grid.drawer-open {
		grid-template-columns: 320px 1fr 320px;
	}

	
	/* RESPONSIVIDADE */
	@media (max-width: 1023px) {
		.whatsapp-main-grid {
			grid-template-columns: 300px 1fr;
		}

		.whatsapp-main-grid.drawer-open {
			grid-template-columns: 280px 1fr 280px;
		}
	}

	@media (max-width: 768px) {
		.whatsapp-header {
			padding: 14px 16px;
		}

		.whatsapp-main-grid {
			grid-template-columns: 1fr;
		}

		.whatsapp-view-container.has-active-chat :global(.wa-sidebar) {
			display: none;
		}

		.whatsapp-view-container:not(.has-active-chat) :global(.wa-conversation) {
			display: none;
		}
	}
</style>
